import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usersAPI, seedAPI, brandingAPI, companiesAPI, quoteTemplatesAPI, tradeAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Settings, Users, Shield, Edit, Database, Mail, Save, Loader2, Building2, UserPlus, Trash2, CreditCard, CheckCircle2, FileText, Upload, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import EmailInboxSettings from '../components/EmailInboxSettings';
import AIKeySettings from '../components/AIKeySettings';

export const SettingsPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('staff');
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    email: '',
    phone: '',
    address: '',
    tax_rate: '10',
    email_signature: '',
  });

  // Template management state
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [uploadTemplateOpen, setUploadTemplateOpen] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateFile, setTemplateFile] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('General');
  const [templateDescription, setTemplateDescription] = useState('');

  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [currentLogo, setCurrentLogo] = useState(null);

  // Custom trades state
  const [tradeTypes, setTradeTypes] = useState([]);
  const [newTradeName, setNewTradeName] = useState('');
  const [addTradeOpen, setAddTradeOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    fetchBranding();
    fetchQuoteTemplates();
    fetchTradeTypes();
  }, []);

  const fetchQuoteTemplates = async () => {
    try {
      const response = await quoteTemplatesAPI.getAll();
      setQuoteTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchTradeTypes = async () => {
    try {
      const response = await tradeAPI.getTradeTypes();
      setTradeTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch trade types:', error);
    }
  };

  const handleUploadTemplate = async () => {
    if (!templateFile || !templateName.trim()) {
      toast.error('Please provide a file and template name');
      return;
    }
    
    setUploadingTemplate(true);
    try {
      const formData = new FormData();
      formData.append('file', templateFile);
      formData.append('template_name', templateName.trim());
      formData.append('template_category', templateCategory);
      formData.append('template_description', templateDescription);
      
      const response = await quoteTemplatesAPI.upload(formData);
      toast.success(`Template "${templateName}" created with ${response.data.items_count} items`);
      
      setUploadTemplateOpen(false);
      setTemplateFile(null);
      setTemplateName('');
      setTemplateCategory('General');
      setTemplateDescription('');
      fetchQuoteTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload template');
    } finally {
      setUploadingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Delete template "${templateName}"?`)) return;
    try {
      await quoteTemplatesAPI.delete(templateId);
      toast.success('Template deleted');
      fetchQuoteTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleAddTrade = async () => {
    if (!newTradeName.trim()) {
      toast.error('Please enter a trade name');
      return;
    }
    try {
      await tradeAPI.createTradeType({ name: newTradeName.trim() });
      toast.success(`Trade type "${newTradeName}" added`);
      setNewTradeName('');
      setAddTradeOpen(false);
      fetchTradeTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add trade type');
    }
  };

  const handleDeleteTrade = async (tradeName) => {
    if (!window.confirm(`Remove custom trade "${tradeName}"?`)) return;
    try {
      await tradeAPI.deleteTradeType(tradeName);
      toast.success('Trade type removed');
      fetchTradeTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove trade type');
    }
  };

  // Check for payment return
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      // Poll for payment status
      pollPaymentStatus(sessionId);
    } else if (payment === 'cancelled') {
      toast.info('Payment was cancelled');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.info('Payment status check timed out. Please refresh the page.');
      return;
    }

    try {
      const response = await api.get(`/billing/checkout-status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        fetchSubscriptionInfo();
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await api.get('/billing/subscription');
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  useEffect(() => {
    fetchSubscriptionInfo();
  }, []);

  const handleSubscribe = async (companyId) => {
    setBillingLoading(true);
    try {
      const response = await api.post('/billing/create-checkout', {
        company_id: companyId,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to start checkout: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBillingLoading(false);
    }
  };

  const fetchBranding = async () => {
    try {
      const response = await brandingAPI.get();
      if (response.data) {
        setCompanySettings(prev => ({
          ...prev,
          company_name: response.data.company_name || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          address: response.data.address || '',
          email_signature: response.data.email_signature || '',
        }));
        setCurrentLogo(response.data.logo_url || null);
      }
    } catch (error) {
      console.error('Failed to fetch branding:', error);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      
      const response = await api.post('/branding/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.logo_url) {
        setCurrentLogo(response.data.logo_url);
        toast.success('Logo uploaded successfully');
        setLogoFile(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companiesAPI.getAll();
      setCompanies(response.data || []);
      if (response.data?.length > 0) {
        // Load first company details
        const companyDetails = await companiesAPI.getOne(response.data[0].company.id);
        setActiveCompany(companyDetails.data);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserActive = async (userId, currentStatus) => {
    try {
      await usersAPI.update(userId, { is_active: !currentStatus });
      toast.success('User status updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email');
      return;
    }
    try {
      await companiesAPI.invite(activeCompany?.id || 'default', { email: inviteEmail, role: inviteRole });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setShowInviteDialog(false);
      setInviteEmail('');
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    }
  };

  const handleRemoveTeamMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    try {
      await companiesAPI.removeUser(activeCompany?.id || 'default', userId);
      toast.success('Team member removed');
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleSaveSettings = async () => {
    setBrandingLoading(true);
    try {
      await brandingAPI.update({
        company_name: companySettings.company_name,
        email: companySettings.email,
        phone: companySettings.phone,
        address: companySettings.address,
        email_signature: companySettings.email_signature,
      });
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setBrandingLoading(false);
    }
  };

  const handleSeedData = async () => {
    try {
      const response = await seedAPI.seedData();
      toast.success(`Sample data created: ${response.data.contractors_created} contractors, ${response.data.jobs_created} jobs`);
    } catch (error) {
      toast.error('Failed to seed data');
    }
  };

  const roleColors = {
    admin: 'bg-primary/20 text-primary border-primary/30',
    staff: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    trade: 'bg-green-500/20 text-green-400 border-green-500/30',
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your account and preferences</p>
      </div>

      {/* AI Provider (company OpenAI key) */}
      <AIKeySettings />

      {/* Email Inbox (AI PO Scanning) */}
      <EmailInboxSettings />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Settings */}
        <Card className="lg:col-span-2 bg-card border-border rounded-lg">
          <CardHeader className="border-b border-border">
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Company Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Logo Upload Section */}
            <div className="border border-border rounded-lg p-4 bg-muted/20">
              <Label className="text-sm font-medium mb-3 block">Company Logo</Label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-background overflow-hidden">
                  {currentLogo ? (
                    <img src={currentLogo} alt="Company Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Building2 className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                    className="bg-input border-border rounded-lg text-sm"
                  />
                  {logoFile && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{logoFile.name}</span>
                      <Button size="sm" onClick={handleLogoUpload} disabled={uploadingLogo}>
                        {uploadingLogo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                        Upload
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">Recommended: 200x200px PNG or JPG. Shown in header and documents.</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={companySettings.company_name}
                  onChange={(e) => setCompanySettings({ ...companySettings, company_name: e.target.value })}
                  placeholder="Your Company Pty Ltd"
                  className="bg-input border-border rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={companySettings.email}
                  onChange={(e) => setCompanySettings({ ...companySettings, email: e.target.value })}
                  placeholder="info@company.com"
                  className="bg-input border-border rounded-lg"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={companySettings.phone}
                  onChange={(e) => setCompanySettings({ ...companySettings, phone: e.target.value })}
                  placeholder="1300 000 000"
                  className="bg-input border-border rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  value={companySettings.tax_rate}
                  onChange={(e) => setCompanySettings({ ...companySettings, tax_rate: e.target.value })}
                  className="bg-input border-border rounded-lg"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={companySettings.address}
                onChange={(e) => setCompanySettings({ ...companySettings, address: e.target.value })}
                placeholder="123 Main Street, City, State 1234"
                className="bg-input border-border rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email Signature
              </Label>
              <Textarea
                value={companySettings.email_signature}
                onChange={(e) => setCompanySettings({ ...companySettings, email_signature: e.target.value })}
                placeholder="Best regards,&#10;Your Name&#10;Company Name&#10;Phone: xxx&#10;Email: xxx"
                className="bg-input border-border rounded-lg min-h-[120px] font-mono text-sm"
                rows={5}
              />
              <p className="text-xs text-muted-foreground">This signature will be automatically added to all outgoing emails.</p>
            </div>
            <Button onClick={handleSaveSettings} disabled={brandingLoading} className="font-semibold rounded-lg">
              {brandingLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save Settings</>}
            </Button>
          </CardContent>
        </Card>

        {/* Profile Card */}
        <Card className="bg-card border-border rounded-lg">
          <CardHeader className="border-b border-border">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Your Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-lg font-bold">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <Badge className={`mt-1 ${roleColors[user?.role]} rounded-lg uppercase text-xs`}>
                  {user?.role}
                </Badge>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              {user?.company_name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Company</span>
                  <span>{user.company_name}</span>
                </div>
              )}
              {user?.phone && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phone</span>
                  <span>{user.phone}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Seed Data */}
      <Card className="bg-card border-border rounded-lg">
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            Sample Data
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-muted-foreground mb-4">
            Populate your system with sample contractors, jobs, and pricing items to get started quickly.
          </p>
          <Button onClick={handleSeedData} variant="outline" className="rounded-lg" data-testid="seed-data-btn">
            <Database className="h-4 w-4 mr-2" />
            Generate Sample Data
          </Button>
        </CardContent>
      </Card>

      {/* User Management */}
      <Card className="bg-card border-border rounded-lg">
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              No users found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="border-border" data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Badge className={`${roleColors[u.role]} rounded-lg uppercase text-xs`}>
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{u.company_name || '-'}</TableCell>
                    <TableCell>
                      <Switch
                        checked={u.is_active}
                        onCheckedChange={() => handleToggleUserActive(u.id, u.is_active)}
                        disabled={u.id === user?.id}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Quote Templates Management */}
      <Card className="bg-card border-border rounded-lg">
        <CardHeader className="border-b border-border flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Quote Templates
          </CardTitle>
          <Button size="sm" onClick={() => setUploadTemplateOpen(true)} data-testid="upload-template-btn">
            <Upload className="h-4 w-4 mr-1" /> Upload Template
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground mb-4">
            Upload CSV or Excel files to create quote templates. Templates can be used to quickly populate quotes for new jobs.
          </p>
          {quoteTemplates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No templates yet</p>
              <Button variant="link" onClick={() => setUploadTemplateOpen(true)}>Upload your first template</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Sections</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quoteTemplates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell><Badge variant="outline">{template.category}</Badge></TableCell>
                    <TableCell>{template.sections?.length || 0}</TableCell>
                    <TableCell>{template.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template.id, template.name)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Custom Trade Types */}
      <Card className="bg-card border-border rounded-lg">
        <CardHeader className="border-b border-border flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Trade Types
          </CardTitle>
          <Button size="sm" onClick={() => setAddTradeOpen(true)} data-testid="add-trade-btn">
            <PlusCircle className="h-4 w-4 mr-1" /> Add Trade
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground mb-4">
            Manage trade types used in quotes, purchase orders, and contractor assignments. Custom trades you add will persist for all future jobs.
          </p>
          <div className="flex flex-wrap gap-2">
            {tradeTypes.map((trade) => {
              const isDefault = ['General', 'Plumber', 'Electrician', 'Carpenter', 'Painter', 'Tiler', 'Roofer', 'Plasterer', 'Bricklayer', 'Landscaper', 'HVAC', 'Glazier', 'Flooring', 'Demolition', 'Concrete'].includes(trade);
              return (
                <Badge key={trade} variant={isDefault ? 'secondary' : 'outline'} className="text-sm py-1 px-3">
                  {trade}
                  {!isDefault && (
                    <button className="ml-2 hover:text-destructive" onClick={() => handleDeleteTrade(trade)}>
                      ×
                    </button>
                  )}
                </Badge>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Billing & Subscription */}
      <Card className="bg-card border-border rounded-lg">
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            Billing & Subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {subscriptionInfo ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Subscription Status</p>
                  <div className="flex items-center gap-2 mt-1">
                    {subscriptionInfo.subscription_status === 'active' ? (
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Active
                      </Badge>
                    ) : subscriptionInfo.subscription_status === 'trial' ? (
                      <Badge className="bg-yellow-500/20 text-yellow-400">Trial</Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400">Inactive</Badge>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Monthly Cost</p>
                  <p className="text-2xl font-bold">${subscriptionInfo.monthly_cost?.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">{subscriptionInfo.user_count} users × $30</p>
                </div>
              </div>
              
              {subscriptionInfo.subscription_status === 'trial' && subscriptionInfo.trial_end && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-sm text-yellow-400">
                    Your trial ends on {new Date(subscriptionInfo.trial_end).toLocaleDateString()}
                  </p>
                </div>
              )}
              
              {subscriptionInfo.subscription_status !== 'active' && (
                <Button 
                  className="w-full" 
                  onClick={() => handleSubscribe(subscriptionInfo.company_id)}
                  disabled={billingLoading}
                >
                  {billingLoading ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                  ) : (
                    <>Subscribe Now - ${subscriptionInfo.monthly_cost?.toFixed(2)}/month</>
                  )}
                </Button>
              )}
              
              {subscriptionInfo.subscription_status === 'active' && subscriptionInfo.subscription_end && (
                <p className="text-sm text-muted-foreground text-center">
                  Next billing: {new Date(subscriptionInfo.subscription_end).toLocaleDateString()}
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-2">Loading subscription info...</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Companies & Team Management */}
      <Card className="bg-card border-border rounded-lg">
        <CardHeader className="border-b border-border flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Team Management
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              $30/month per user
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {companies.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No company configured</p>
          ) : (
            <div className="space-y-6">
              {companies.map((c) => (
                <div key={c.company?.id} className="border border-border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-lg">{c.company?.name}</h3>
                      <p className="text-sm text-muted-foreground">Your role: <span className="capitalize text-primary">{c.role}</span></p>
                      {c.company?.team_count > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {c.company.team_count} team member{c.company.team_count !== 1 ? 's' : ''} • 
                          Est. ${(c.company.team_count * 30).toFixed(0)}/month
                        </p>
                      )}
                    </div>
                    {(c.role === 'owner' || c.role === 'admin') && (
                      <Button size="sm" variant="outline" onClick={() => { setActiveCompany(c.company); setShowInviteDialog(true); }}>
                        <UserPlus className="h-4 w-4 mr-1" /> Add User
                      </Button>
                    )}
                  </div>
                  
                  {activeCompany?.id === c.company?.id && activeCompany?.team && (
                    <div>
                      <h4 className="text-sm font-medium mb-2">Team Members</h4>
                      <div className="space-y-2">
                        {activeCompany.team.map((member) => (
                          <div key={member.user?.id} className="flex items-center justify-between p-2 bg-secondary/30 rounded">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                <span className="text-sm font-bold text-primary">
                                  {member.user?.name?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium">{member.user?.name}</p>
                                <p className="text-xs text-muted-foreground">{member.user?.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="capitalize text-xs">{member.role}</Badge>
                              <span className="text-xs text-muted-foreground">$30/mo</span>
                              {(c.role === 'owner' || c.role === 'admin') && member.role !== 'owner' && member.user?.id !== user?.id && (
                                <Button size="icon" variant="ghost" onClick={() => handleRemoveTeamMember(member.user?.id)}>
                                  <Trash2 className="h-4 w-4 text-red-400" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite User Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-primary/10 p-3 rounded-lg text-sm">
              <p className="font-medium">Pricing: $30/month per user</p>
              <p className="text-muted-foreground text-xs mt-1">Each team member added will increase your monthly subscription.</p>
            </div>
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@example.com"
                className="bg-background"
              />
              <p className="text-xs text-muted-foreground mt-1">User must already have an account</p>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
            <Button onClick={handleInviteUser}>Add User ($30/mo)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Template Dialog */}
      <Dialog open={uploadTemplateOpen} onOpenChange={setUploadTemplateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Quote Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV or Excel file to create a reusable quote template. The file should have columns for Description, Quantity, Price, Trade, and optionally Section/Area.
            </p>
            <div>
              <Label>Template Name *</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Bathroom Renovation, Kitchen Fit-out"
                className="bg-background"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={templateCategory} onValueChange={setTemplateCategory}>
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">General</SelectItem>
                  <SelectItem value="Renovation">Renovation</SelectItem>
                  <SelectItem value="New Build">New Build</SelectItem>
                  <SelectItem value="Extension">Extension</SelectItem>
                  <SelectItem value="Repairs">Repairs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Brief description of this template..."
                className="bg-background"
              />
            </div>
            <div>
              <Label>File (CSV or Excel) *</Label>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setTemplateFile(e.target.files?.[0] || null)}
                className="bg-background"
              />
              {templateFile && (
                <p className="text-xs text-muted-foreground mt-1">Selected: {templateFile.name}</p>
              )}
            </div>
            <div className="bg-muted/50 p-3 rounded-lg text-xs text-muted-foreground">
              <p className="font-medium mb-1">Expected columns:</p>
              <p>• <strong>Description</strong> (required) - Item description</p>
              <p>• <strong>Quantity</strong> - Default quantity</p>
              <p>• <strong>Price/Rate</strong> - Unit price</p>
              <p>• <strong>Trade</strong> - Trade type (Plumber, Electrician, etc.)</p>
              <p>• <strong>Section/Area</strong> - Group items by section</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadTemplateOpen(false); setTemplateFile(null); setTemplateName(''); }}>
              Cancel
            </Button>
            <Button onClick={handleUploadTemplate} disabled={uploadingTemplate || !templateFile || !templateName.trim()}>
              {uploadingTemplate ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Uploading...</> : 'Upload Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Trade Dialog */}
      <Dialog open={addTradeOpen} onOpenChange={setAddTradeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Trade Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Add a custom trade type that will be available for all quotes, purchase orders, and contractor assignments.
            </p>
            <div>
              <Label>Trade Name</Label>
              <Input
                value={newTradeName}
                onChange={(e) => setNewTradeName(e.target.value)}
                placeholder="e.g., Solar Installer, Pool Builder, Scaffolder"
                className="bg-background"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddTradeOpen(false); setNewTradeName(''); }}>
              Cancel
            </Button>
            <Button onClick={handleAddTrade} disabled={!newTradeName.trim()}>
              Add Trade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
